import React, { useState, useEffect } from 'react';
import { Modal, message } from 'antd';
import TimePicker from 'components/TimePicker';
import dayjs from 'utils/dayjs';
import { TServerCalendarAppointmentEventDetail } from 'types/calendar';
import { calendarCreateOrUpdateAppointment, getProviderTimeSlotByLength } from 'api/google';
import s from './s.module.less';
import { Prospect } from 'types/common';
import useTimezone from 'hooks/useTimezone';

interface RescheduleModalProps {
    open: boolean;
    onReopen: () => void;
    onClose: () => void;
    onSuccess: () => void;
    appointmentEventDetail: TServerCalendarAppointmentEventDetail;
    targetClient: Prospect;
}
const RescheduleModal: React.FC<RescheduleModalProps> = (props) => {
    const { open, onReopen, onClose, onSuccess, appointmentEventDetail, targetClient } = props;

    const [, myTimezone] = useTimezone();
    const timezone: string = myTimezone?.value as string;

    const [rescheduleTime, setRescheduleTime] = useState<number>();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [rescheduling, setRescheduling] = useState<boolean>(false);
    const [timeSlotList, setTimeSlotList] = useState<number[]>([]);
    const [loadingTimeSlot, setLoadingTimeSlot] = useState<boolean>(false);

    const followUpDuration = 15;

    React.useEffect(() => {
        if (appointmentEventDetail) {
            const startAt = dayjs().add(-1, 'days').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
            const endAt = dayjs().add(60, 'days').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

            const fetchData = async () => {
                setLoadingTimeSlot(true);
                const res = await getProviderTimeSlotByLength({ startAt, endAt, length: followUpDuration || 15 });
                setLoadingTimeSlot(false);
                if (!res.error) {
                    const serverSlog: { date: string; length: string }[] = res.data?.data?.timeSlots;
                    const addTime: number[] = [];
                    serverSlog?.forEach((item) => {
                        const date = item.date?.replace(' +0000', 'Z');
                        if (new Date(date).getTime() > new Date().getTime()) {
                            addTime.push(dayjs(date).valueOf());
                        }
                    });
                    setTimeSlotList(addTime);
                    const expectRescheduleTime = dayjs(appointmentEventDetail.startAt).add(1, 'days').valueOf();

                    let defaultTimeForRechedule = 0;
                    addTime.forEach((item) => {
                        if (
                            Math.abs(item - expectRescheduleTime) <
                            Math.abs(defaultTimeForRechedule - expectRescheduleTime)
                        ) {
                            defaultTimeForRechedule = item;
                        }
                    });
                    setRescheduleTime(defaultTimeForRechedule);
                }
            };
            fetchData();
        }
    }, [appointmentEventDetail, timezone]);

    const handleRescheduleConfirm = async () => {
        if (!appointmentEventDetail || !rescheduleTime) {
            return;
        }
        const data: TServerCalendarAppointmentEventDetail | undefined = { ...appointmentEventDetail };

        const startDayjs = dayjs(rescheduleTime).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
        const endDayjs = dayjs(rescheduleTime)
            .add(appointmentEventDetail.duration, 'minute')
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss[Z]');

        data.startAt = startDayjs;
        data.endAt = endDayjs;

        try {
            setRescheduling(true);
            const result = await calendarCreateOrUpdateAppointment(data);

            setRescheduling(false);

            if (!result.error) {
                message.success('Appointment rescheduled successfully.');
                setShowConfirmModal(false);
                onSuccess?.();
            }
        } catch (e) {
            console.error('Error rescheduling appointment:', e);
        } finally {
            setRescheduling(false);
        }
    };

    /**
     * 生成服务标签
     *
     * 格式: [State] Specialty: Service Type | Duration mins
     * 例如: [NY] Mental Health: Initial Consultation | 60 mins
     */
    const rescheduleServiceLabel = React.useMemo(() => {
        if (!appointmentEventDetail) return '';

        let label = '';

        if (appointmentEventDetail.serviceState) {
            label = `${label}[${appointmentEventDetail.serviceState}]`;
        }
        if (appointmentEventDetail.serviceSpecialty) {
            label = `${label} ${appointmentEventDetail.serviceSpecialty}:`;
        }
        if (appointmentEventDetail.serviceType) {
            label = `${label} ${appointmentEventDetail.serviceType}`;
        }
        if (appointmentEventDetail.duration) {
            label = `${label} | ${appointmentEventDetail.duration} mins`;
        }

        return label.trim();
    }, [appointmentEventDetail]);

    /**
     * 生成日期和时间标签
     *
     * 日期格式: Sunday, March 2, 2025
     * 时间格式: 10:00 - 10:30 AM
     */
    const { rescheduleDateLabel, rescheduleTimeLabel } = React.useMemo(() => {
        if (!rescheduleTime) {
            return { rescheduleDateLabel: '', rescheduleTimeLabel: '' };
        }

        try {
            let rescheduleDayjs = dayjs(rescheduleTime);

            // 应用时区
            if (timezone) {
                rescheduleDayjs = rescheduleDayjs.tz(timezone);
            }

            // 格式化日期: Sunday, March 2, 2025
            const dateLabel = rescheduleDayjs.format('dddd, MMMM D, YYYY');

            // 格式化时间: 10:00 - 10:30 AM
            const timeLabel = `${rescheduleDayjs.format(rescheduleDayjs.hour() === 0 ? 'HH:mm' : 'hh:mm')} - ${rescheduleDayjs.add(appointmentEventDetail?.duration || 0, 'minute').format(rescheduleDayjs.hour() === 0 ? 'HH:mm' : 'hh:mm A')}`;

            return { rescheduleDateLabel: dateLabel, rescheduleTimeLabel: timeLabel };
        } catch (e) {
            console.error('Error calculating reschedule date:', e);
            return { rescheduleDateLabel: '', rescheduleTimeLabel: '' };
        }
    }, [rescheduleTime, timezone, appointmentEventDetail]);

    return (
        <>
            <Modal
                open={open}
                onCancel={onClose}
                className={s.modalWrap}
                wrapClassName="practice"
                width={660}
                onOk={() => {
                    onClose();
                    setShowConfirmModal(true);
                }}
                okText="Schedule"
            >
                <div className={s.modalWrap}>
                    <div className={`${s.timePicker} ${s.timePickerShow}`}>
                        {/* 标题 */}
                        <div className={s.modalTitle}>
                            <h3>Select date and time</h3>
                            <div className={s.subTitle}>{timezone ? `Timezone: ${timezone}` : ''}</div>
                        </div>

                        {/* 服务信息 */}
                        <div className={s.modalService}>
                            <span className={s.modalServiceLabel}>Service:</span> {rescheduleServiceLabel}
                        </div>

                        {/* 时间选择器 */}
                        <div className={s.timePickerIn}>
                            <TimePicker
                                idSub="modal"
                                onChange={(time: number) => {
                                    setRescheduleTime(time);
                                }}
                                timeSlotList={timeSlotList}
                                timezone={timezone}
                                extraTop={100}
                                defaultValue={rescheduleTime}
                                loading={loadingTimeSlot}
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                open={showConfirmModal}
                onCancel={() => {
                    setShowConfirmModal(false);
                }}
                wrapClassName="practice"
                width={660}
                onOk={() => {
                    handleRescheduleConfirm();
                }}
                okText="Confirm"
                okButtonProps={{
                    loading: rescheduling,
                }}
            >
                <div className={s.modalWrap}>
                    <div className={`${s.timePicker} ${s.timePickerShow}`}>
                        <div className={s.modalTitle}>
                            <h3>Confirm appointment</h3>
                        </div>

                        <div
                            onClick={() => {
                                setShowConfirmModal(false);
                                onReopen();
                            }}
                            className={s.modalBack}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M6.66667 12.6654L2 7.9987M2 7.9987L6.66667 3.33203M2 7.9987L14 7.9987"
                                    stroke="#0E62F4"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Back
                        </div>

                        <div className={s.modalDes}>
                            You have scheduled a new appointment for patient {targetClient?.firstName}{' '}
                            {targetClient?.lastName}. Please review and confirm the appointment details below:
                        </div>

                        <div className={s.modalUpdate}>
                            <div className={s.muItem}>
                                <div className={s.muItemLabel}>Service:</div>
                                <div className={s.muItemValue}>{rescheduleServiceLabel}</div>
                            </div>
                            <div className={s.muItem}>
                                <div className={s.muItemLabel}>Date:</div>
                                <div className={s.muItemValue}>{rescheduleDateLabel}</div>
                            </div>
                            <div className={s.muItem}>
                                <div className={s.muItemLabel}>Time:</div>
                                <div className={s.muItemValue}>{rescheduleTimeLabel}</div>
                            </div>
                            <div className={s.muItem} />
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default RescheduleModal;
